import Logout from 'src/assets/images/nav/logout.svg'
import PersonIcon from 'src/assets/images/nav/person.svg'
import PersonDarkIcon from 'src/assets/images/nav/person-dark.svg'
import { getCurrentRoles } from 'src/components/app/utils/accessControl/accessControlUtils'
import { getStringifiedOwnerRoles } from 'src/pages/permissions/utils'
import { useAppDispatch, useAppSelector } from 'src/redux/app/app.hooks'
import { logoutUser, selectCurrentUser, selectCurrentUserRoles } from 'src/redux/auth'
import { Row } from 'src/ui-kit/layout/Layout'
import { TooltipMenu } from 'src/ui-kit/tooltipMenu/TooltipMenu'
import { VoiIcon } from 'src/ui-kit/voiIcon/VoiIcon'
import CopyButton from 'src/ui-kit/copyButton/CopyButton'
import styled from '@emotion/styled'
import { copyToClipboard } from 'src/utils/copyToClipboard/copyToClipboardUtils'

interface Props {
    useDarkIcon?: boolean
}

export const RolesAndLogout = (props: Props) => {
    const { useDarkIcon = false } = props
    const dispatch = useAppDispatch()

    const currentUser = useAppSelector(selectCurrentUser)
    const roles = useAppSelector(selectCurrentUserRoles)

    const voiOrgRoles = getCurrentRoles(roles)
    const roleList = getStringifiedOwnerRoles(voiOrgRoles)
    const photoUrl = currentUser?.photoUrl // Either the user's Google account photo or Google's placeholder image
    const accountId = currentUser?.userId

    return (
        <TooltipMenu
            placement='left'
            closeOnClickMenuItem={false}
            anchor={
                photoUrl ? (
                    <PhotoIcon src={photoUrl} data-testid='logout-tooltip' />
                ) : (
                    <VoiIcon src={useDarkIcon ? PersonDarkIcon : PersonIcon} data-testid='logout-tooltip' />
                )
            }
        >
            {accountId && (
                <Row onClick={() => copyToClipboard(accountId)}>
                    <AccountId data-testid='logout-account-id'>
                        <CopyButton isHighlighted={false} value={accountId}>
                            <AccountIdLabel>ACCOUNT ID</AccountIdLabel>
                        </CopyButton>
                        <AccountIdValue>{accountId}</AccountIdValue>
                    </AccountId>
                </Row>
            )}
            <Row>
                <AccountIdLabel>YOUR ROLES</AccountIdLabel>
                <RoleList data-testid='logout-user-role'>{roleList}</RoleList>
            </Row>
            <Row data-testid='logout-button' onClick={() => dispatch(logoutUser())}>
                <VoiIcon src={Logout} style={{ marginRight: '10px' }} />
                <LogoutText>LOGOUT</LogoutText>
            </Row>
        </TooltipMenu>
    )
}

const RoleList = styled.span`
    display: flex;
    font-family: Montserrat, monospace;
    line-height: 1.24;
    font-weight: 700;
    font-size: 12px;
    white-space: initial;
`

const AccountId = styled.div`
    display: flex;
    flex-direction: column;

    /* A smaller copy icon than the ui-kit default, to suit the small ID text. */
    img {
        height: 16px;
        width: 16px;
        margin-left: 8px;
    }
`

const AccountIdLabel = styled.span`
    font-family: 'Overpass Mono', monospace;
    line-height: 14px;
    font-weight: 700;
    font-size: 10px;
    opacity: 0.6;
`

const AccountIdValue = styled.span`
    font-family: 'Overpass Mono', monospace;
    line-height: 14px;
    font-size: 10px;
    white-space: initial;
    word-break: break-all;

    /* Lets the reader select the whole ID with one click, as well as copy it. */
    user-select: all;
`

const LogoutText = styled.span`
    font-family: 'Overpass Mono', monospace;
    line-height: 14px;
    font-weight: 700;
`

const PhotoIcon = styled.img`
    width: 30px;
    border-radius: 15px;
`
